import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Eye } from "lucide-react";

/**
 * Callouts — read-only domain view.
 *
 * Canon: information enters the system through one slot only — the Shredder.
 * Direct upload was removed 13 May 2026 (No. 6 directive: "information only
 * goes through the shredder"). This page now shows whatever the Shredder has
 * classified into `domain: callout` and links back to the intake desk.
 */
export default function Callouts() {
    const [recent, setRecent] = useState([]);

    const load = async () => {
        const { data } = await api.get("/shed/ledger", { params: { domain: "callout", limit: 100 } });
        setRecent(data.entries || []);
    };
    useEffect(() => { load(); }, []);

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Ops Desk · Who's Watching</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                        <Eye className="w-10 h-10 text-stamp-red" strokeWidth={1.5} />
                        Callouts<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Named watchers — FA, PwC, Deloitte, Lisbon — filed against the post
                        that caught their eye. All entries flow in via the Shredder; to add
                        a new screenshot, post it through the slot.
                    </p>
                </div>
                <div className="pencil text-lg transform rotate-2">
                    "The knitting brings them in." — No. 6
                </div>
            </div>

            <div className="paper-card p-4 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="callouts-intake-banner">
                <div className="label-typed tw-prefix">
                    Intake routed through the Shredder · one slot, one classifier
                </div>
                <Link
                    to="/shredder"
                    data-testid="callouts-shredder-link"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] border border-stamp-red text-stamp-red px-3 py-1.5 hover:bg-stamp-red hover:text-paper"
                >
                    Open Shredder →
                </Link>
            </div>

            <div className="label-typed tw-prefix mb-3">Filed callouts — {recent.length}</div>
            {recent.length === 0 ? (
                <div className="paper-card border border-dashed border-ink/30 p-12 text-center tilt-l" data-testid="callouts-empty">
                    <div className="font-heading text-2xl uppercase tracking-[0.06em] text-ink-faint">No watchers yet</div>
                    <div className="label-typed mt-2">
                        Post a LinkedIn analytics screenshot through the Shredder — it will land here
                        once classified as <span className="text-stamp-red">callout</span>.
                    </div>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="callouts-list">
                    {recent.map((e) => {
                        const s = e.structured || {};
                        return (
                            <div key={e.id} className="paper-card p-4" data-testid={`callout-${e.id}`}>
                                <div className="label-typed mb-1">
                                    {(s.detected_on || e.timestamp || "").slice(0, 10)} · {s.signal_type || "viewed"}
                                </div>
                                <div className="font-heading text-lg text-ink tracking-[0.04em]">
                                    {s.watcher_org || s.watcher_name || "Unnamed watcher"}
                                </div>
                                {s.watcher_name && s.watcher_org && (
                                    <div className="font-body text-xs text-ink-soft">
                                        {s.watcher_name} {s.watcher_title ? `· ${s.watcher_title}` : ""}
                                    </div>
                                )}
                                <div className="font-body text-xs text-ink mt-2">{e.summary}</div>
                                {s.linked_post_id && (
                                    <div className="mt-2 label-typed text-[9px]">→ post: {s.linked_post_id}</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Shell>
    );
}
